/**
 * UIControlHandlerRegistry
 *
 * Global registry for UI control handlers. Allows page-level AgentStateProviders
 * to register their handlers, and the global PlatformAssistant to access them.
 *
 * This solves the architectural challenge where:
 * - PlatformAssistant lives in the global layout (EducatorAppLayout)
 * - AgentStateProvider lives in individual pages
 * - The panel needs access to the current page's context
 *
 * Pattern: Singleton registry with pub/sub for handler changes
 */

import type { UIControlHandlers } from '../hooks/usePlatformAssistantChat';

type HandlerChangeListener = (handlers: UIControlHandlers | null) => void;

class UIControlHandlerRegistryClass {
  private currentHandlers: UIControlHandlers | null = null;
  private listeners: Set<HandlerChangeListener> = new Set();

  /**
   * Register UI control handlers (called by AgentStateProvider on mount)
   */
  register(handlers: UIControlHandlers): void {
    this.currentHandlers = handlers;
    this.notifyListeners();
  }

  /**
   * Unregister handlers (called by AgentStateProvider on unmount)
   */
  unregister(): void {
    this.currentHandlers = null;
    this.notifyListeners();
  }

  /**
   * Get current handlers (for one-time access)
   */
  getHandlers(): UIControlHandlers | null {
    return this.currentHandlers;
  }

  /**
   * Subscribe to handler changes
   * Returns unsubscribe function
   */
  subscribe(listener: HandlerChangeListener): () => void {
    this.listeners.add(listener);

    // Immediately notify with current state
    listener(this.currentHandlers);

    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.currentHandlers));
  }
}

// Singleton instance
export const UIControlHandlerRegistry = new UIControlHandlerRegistryClass();